import { type ApiSchemas, call, type Result } from '@rental/contracts/client'
import { FINE_CHANNELS, type FineChannel } from '@rental/domain'
import { format, formatDate, formatDateTime, formatEuro, type StaffMessages } from '@rental/i18n'
import { Button, cn, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeft, FileText } from 'lucide-react'
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { AUTH_MODE, api, load } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { ROW_STILL } from '../components/Results'
import { ArrivalNotice } from '../components/Said'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { ReasonList } from '../components/SubPanel'
import { Guard, RecordPending } from '../components/states'
import { Facts, FIELD, FIELD_ROW, ProblemNotice, StaffInput, StaffSelect } from '../components/ui'
import { filePath } from '../files'
import { FineLetterPdf } from '../files-ui'
import { FineForm } from '../fines/FineForm'
import {
  deadlineTone,
  deadlineWords,
  endingsFrom,
  type FineEnding,
  REASON_MIN,
  reasonRequired,
  statusTone,
} from '../fines/fines'
import { fineChanged, fineQueryKey } from '../fines/queries'
import { useSend } from '../fines/send'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { problemOf, type StaffProblem } from '../problems'
import { BookingCode } from '../reports/BookingCode'
import { romeNow } from '../time'

const route = getRouteApi('/fines/$fineId')

type StaffFine = ApiSchemas['StaffFine']
type Done = keyof StaffMessages['fines']['done']
type Changed = (fine: StaffFine, done: Done) => Promise<void>
/** A part of the fine, with what was done there just now, said in it. */
type BlockProps = { fine: StaffFine; onChanged: Changed; said: ReactNode }

/** How often a letter being made is asked after: the worker renders it in seconds. */
export const LETTER_POLL_MS = 2_000

/**
 * S6 (CMP-2, decision 4): one fine, for the holders of fines.manage. The
 * notice as registered; the rental that had the car, found by itself or
 * picked; the letter naming the renter; the day it was sent to the authority;
 * the admin fee; and how it ended. Each button is a move the domain allows
 * from where the fine stands; the server decides. UI-2e: a read that fails
 * while the letter is awaited keeps the fine on screen and says so above it.
 */
export function FineScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'fines.manage')
  const { fineId } = route.useParams()
  const search = route.useSearch()
  const fine = useQuery({
    queryKey: fineQueryKey(fineId),
    queryFn: () =>
      load(call(api.GET('/v1/staff/fines/{id}', { params: { path: { id: fineId } } }))),
    enabled: allowed,
    // While the worker makes the letter, the page asks until it is there.
    refetchInterval: (query) =>
      query.state.data?.fine.letter.state === 'preparing' ? LETTER_POLL_MS : false,
  })
  const problem = problemOf(fine.error)
  const back = (
    <Link to="/fines" className="flex min-h-touch items-center gap-1 self-start">
      <ChevronLeft aria-hidden="true" className="size-4" />
      {m.fines.detail.back}
    </Link>
  )
  return (
    <Guard allowed={allowed} title={m.fines.detail.heading}>
      {fine.data ? (
        <FineDetail
          fine={fine.data.fine}
          back={back}
          problem={problem}
          registered={search.done === 'registered'}
        />
      ) : (
        <RecordPending
          back={back}
          title={m.fines.detail.heading}
          problem={problem}
          notFound={m.fines.detail.notFound}
        />
      )}
    </Guard>
  )
}

function FineDetail({
  fine,
  back,
  problem,
  registered,
}: {
  fine: StaffFine
  back: ReactNode
  /** A refresh that failed: said, while the fine as last read stays. */
  problem: StaffProblem | null
  registered: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const copy = m.fines
  const queryClient = useQueryClient()
  // What a press here just did: the button that did it is often gone.
  const [done, setDone] = useState<{ what: Done; at: number } | null>(null)
  const [correcting, setCorrecting] = useState(false)
  const notice = useRef<HTMLDivElement>(null)
  const moved = useRef<'opened' | 'cancelled' | null>(null)

  // The correction takes the focus as it opens; Annulla gives it back to its button.
  useEffect(() => {
    if (moved.current === 'opened' && correcting)
      notice.current?.querySelector<HTMLElement>('form input')?.focus()
    if (moved.current === 'cancelled' && !correcting) {
      notice.current?.querySelector<HTMLElement>('[data-correct]')?.focus()
    }
    moved.current = null
  }, [correcting])

  async function changed(next: StaffFine, what: Done) {
    await fineChanged(queryClient, next)
    setDone({ what, at: Date.now() })
  }
  // What a press did is said where it was pressed, as on every other screen
  // (OutcomeNotice, UI-2g): the focus stays by the button, the page does not jump up.
  const said = (...whats: Done[]) =>
    done && whats.includes(done.what) ? (
      <OutcomeNotice key={done.at}>
        <p>{copy.done[done.what]}</p>
      </OutcomeNotice>
    ) : null

  const vehicle = fine.vehicle ? (
    can(access, 'fleet.read') ? (
      <Link
        key="vehicle"
        to="/vehicles/$vehicleId"
        params={{ vehicleId: fine.vehicle.id }}
        className="touch-target"
      >
        {fine.vehicle.make} {fine.vehicle.model}
      </Link>
    ) : (
      `${fine.vehicle.make} ${fine.vehicle.model}`
    )
  ) : (
    <span key="vehicle" className="text-text-muted">
      {copy.detail.notOurPlate}
    </span>
  )

  return (
    <>
      <PageHeader
        back={back}
        title={format(copy.detail.title, { number: fine.noticeNumber })}
        status={<StatusChip tone={statusTone(fine.status)}>{copy.status[fine.status]}</StatusChip>}
      />
      <ProblemNotice problem={problem} />
      {/* One above the other, full width: two of them hold forms. */}
      <Section title={copy.detail.notice}>
        {said('corrected') ??
          (registered && done === null ? (
            // Arrived from the register's form: the page's title takes the focus, this is said
            // after it (UI-2g: a status drawn with its words was not read).
            <ArrivalNotice>
              <p>{copy.done.registered}</p>
            </ArrivalNotice>
          ) : null)}
        <div ref={notice} className="flex flex-col gap-3">
          <Facts
            items={[
              [copy.detail.authority, fine.authority],
              [
                copy.detail.violated,
                format(copy.detail.when, {
                  date: formatDate(locale, fine.violatedOn),
                  time: fine.violatedAt,
                }),
              ],
              [copy.detail.place, fine.place],
              [
                copy.detail.plate,
                <span key="plate" className="figures">
                  {fine.plate}
                </span>,
              ],
              [copy.detail.vehicle, vehicle],
              [
                copy.detail.amount,
                <span key="amount" className="figures">
                  {formatEuro(fine.amountCents, locale)}
                </span>,
              ],
              [copy.detail.notified, formatDate(locale, fine.notifiedOn)],
              [
                copy.detail.deadline,
                <span key="deadline" className="flex flex-wrap items-center gap-2">
                  {formatDate(locale, fine.deadline.on)}
                  <StatusChip tone={deadlineTone(fine.deadline.state)}>
                    {deadlineWords(fine.deadline, locale, m)}
                  </StatusChip>
                </span>,
              ],
            ]}
          />
          <p className="text-sm text-text-muted">
            {format(copy.detail.registered, {
              name: fine.registeredBy,
              when: formatDateTime(locale, fine.createdAt),
            })}
          </p>
          {fine.status !== 'registered' ? null : correcting ? (
            <FineForm
              correcting={fine}
              surface="well"
              onDone={async (next) => {
                setCorrecting(false)
                await changed(next, 'corrected')
              }}
              onCancel={() => {
                moved.current = 'cancelled'
                setCorrecting(false)
              }}
            />
          ) : (
            <Button
              variant="outline"
              className="self-start rounded-sm"
              data-correct=""
              onClick={() => {
                moved.current = 'opened'
                setDone(null)
                setCorrecting(true)
              }}
            >
              {copy.detail.correct}
            </Button>
          )}
        </div>
      </Section>
      <RentalBlock fine={fine} onChanged={changed} said={said('matched', 'unmatched')} />
      {fine.rental ? <LetterBlock fine={fine} onChanged={changed} said={said('letter')} /> : null}
      <CommunicationBlock fine={fine} onChanged={changed} said={said('communicated')} />
      <FeeBlock fine={fine} onChanged={changed} said={said('fee')} />
      <EndBlock fine={fine} onChanged={changed} said={said('ended', 'reopened')} />
    </>
  )
}

/** A rental that had the car: its booking, its renter and how long they had it. */
function RentalLine({ rental }: { rental: ApiSchemas['FineRental'] }) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  // S8: an anonymised renter names nobody: said in words, never as a bare mark.
  const name = rental.customer.anonymised ? m.customer.anonymisedTitle : rental.customer.name
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <BookingCode code={rental.code} />
      {can(access, 'customers.read') ? (
        <Link
          to="/customers/$customerId"
          params={{ customerId: rental.customer.id }}
          className="touch-target"
        >
          {name}
        </Link>
      ) : (
        <span>{name}</span>
      )}
      <span className="text-sm text-text-muted">
        {format(m.fines.rental.period, {
          from: formatDateTime(locale, rental.checkedOutAt),
          to: rental.returnedAt
            ? formatDateTime(locale, rental.returnedAt)
            : m.fines.rental.stillOut,
        })}
      </span>
    </div>
  )
}

/**
 * A move that asks why, in a dialog: undoing a match, ruling a notice out,
 * a withdrawal, closing, reopening. The dialog closes before the page shows
 * the fine as it now stands, so nothing open is pulled from under the person.
 * Its button says what it waits for: a reason of three letters at least.
 */
function ReasonAction({
  label,
  hint,
  required,
  request,
  done,
  onChanged,
}: {
  label: string
  hint?: string
  required: boolean
  request: (reason: string | undefined) => Promise<Result<{ fine: StaffFine }>>
  done: Done
  onChanged: Changed
}) {
  const { messages: m } = useLocale()
  const dialog = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState('')
  const { send, busy, problem, clear } = useSend()
  const text = reason.trim()
  const short = text.length > 0 && text.length < REASON_MIN
  const ready = required ? text.length >= REASON_MIN : !short

  async function confirm() {
    const answer = await send(() => request(text || undefined))
    if (!answer) return
    dialog.current?.close()
    await onChanged(answer.fine, done)
  }

  return (
    <>
      <Button
        variant="outline"
        className="self-start rounded-sm"
        onClick={() => {
          setReason('')
          clear()
          dialog.current?.showModal()
        }}
      >
        {label}
      </Button>
      <ConfirmDialog
        dialogRef={dialog}
        title={label}
        confirmLabel={m.fines.end.submit}
        cancelLabel={m.fines.end.cancel}
        busy={busy}
        ready={ready}
        reasons={[format(m.fines.end.why, { min: REASON_MIN })]}
        onConfirm={() => void confirm()}
      >
        {hint ? <p>{hint}</p> : null}
        <Field
          label={m.fines.end.reason}
          required={required}
          optionalLabel={required ? undefined : m.newBooking.optional}
          error={short ? m.refund.reasonShort : null}
        >
          {(control) => (
            <StaffInput
              {...control}
              maxLength={500}
              autoComplete="off"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
        <ProblemNotice problem={problem} />
      </ConfirmDialog>
    </>
  )
}

/**
 * The rental that had the car at the violation's minute: named by itself when
 * only one did, picked by the Owner when several did, or none (the plate is
 * not ours, or the car was at the office). While a pick is on its way every
 * pick waits, the pressed one busy, none of them natively disabled: the focus
 * stays where it was pressed (UI-2e).
 */
function RentalBlock({ fine, onChanged, said }: BlockProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.fines.rental
  const { send, busy, problem } = useSend()
  const [picking, setPicking] = useState<string | null>(null)

  async function pick(rentalId: string) {
    if (busy) return
    setPicking(rentalId)
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/fines/{id}/match', {
          params: { path: { id: fine.id } },
          body: { rentalId },
        }),
      ),
    )
    setPicking(null)
    if (answer) await onChanged(answer.fine, 'matched')
  }

  let body: ReactNode
  if (fine.rental) {
    body = (
      <>
        <RentalLine rental={fine.rental} />
        {fine.match ? (
          <p className="text-sm text-text-muted">
            {fine.match.kind === 'auto'
              ? copy.auto
              : format(copy.manual, {
                  name: fine.match.by,
                  when: formatDateTime(locale, fine.match.at),
                })}
          </p>
        ) : null}
        {fine.status === 'matched' && !fine.fee.charged ? (
          <ReasonAction
            label={copy.unmatch}
            hint={copy.unmatchHint}
            required
            done="unmatched"
            onChanged={onChanged}
            request={(reason) =>
              call(
                api.POST('/v1/staff/fines/{id}/unmatch', {
                  params: { path: { id: fine.id } },
                  body: { reason: reason ?? '' },
                }),
              )
            }
          />
        ) : null}
      </>
    )
  } else if (fine.status !== 'registered') {
    body = <p className="text-text-muted">{copy.noneNamed}</p>
  } else if (!fine.vehicle) {
    body = (
      <Notice tone="warning">
        <p>{copy.noVehicle}</p>
      </Notice>
    )
  } else if (fine.candidates.length === 0) {
    body = (
      <Notice tone="warning">
        <p>{copy.none}</p>
      </Notice>
    )
  } else {
    body = (
      <>
        {fine.candidates.length > 1 ? <p>{copy.several}</p> : null}
        <ul className="flex flex-col" data-testid="fine-candidates">
          {fine.candidates.map((candidate) => (
            <li key={candidate.rentalId} className={cn(ROW_STILL, 'gap-2')}>
              <RentalLine rental={candidate} />
              <Button
                variant="staff"
                className="self-start"
                busy={picking === candidate.rentalId}
                aria-disabled={busy || undefined}
                onClick={() => void pick(candidate.rentalId)}
              >
                {format(copy.pick, { code: candidate.code })}
              </Button>
            </li>
          ))}
        </ul>
        <ProblemNotice problem={problem} />
      </>
    )
  }
  return (
    <Section title={copy.title}>
      {said}
      {body}
    </Section>
  )
}

/** The letter, in the web app a link that sends the session's cookies; in the iPad app a fetch. */
function LetterLink({ fine }: { fine: StaffFine }) {
  const { messages: m } = useLocale()
  if (AUTH_MODE === 'token') {
    return <FineLetterPdf fineId={fine.id} noticeNumber={fine.noticeNumber} />
  }
  return (
    <a
      href={filePath({ kind: 'fineLetter', fineId: fine.id })}
      target="_blank"
      rel="noopener"
      className="flex min-h-touch items-center gap-2 self-start"
    >
      <FileText aria-hidden="true" className="size-6" />
      {m.fines.letter.open}
    </a>
  )
}

/**
 * The letter naming the renter, made by the worker; asked again if it gave up.
 * S8: by the server's own rules, none is offered once the renter was
 * anonymised (the fine is handled by hand), and a letter on a rental naming
 * someone anonymised opens only for customers.privacy.
 */
function LetterBlock({ fine, onChanged, said }: BlockProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.fines.letter
  const { access } = useAccess()
  const { send, busy, problem } = useSend()
  const canAsk = fine.status === 'matched' || fine.status === 'communicated'
  const { letter } = fine
  const unaskable =
    letter.renterAnonymised && (letter.state === 'none' || letter.state === 'failed')
  const opens = !letter.ownerOnly || can(access, 'customers.privacy')

  async function ask() {
    const answer = await send(() =>
      call(api.POST('/v1/staff/fines/{id}/letter', { params: { path: { id: fine.id } } })),
    )
    if (answer) await onChanged(answer.fine, 'letter')
  }

  // The letter's state in one status, there in every state, so its coming is heard: in
  // preparation, then ready or not made (UI-2g). Two statuses that swapped were not.
  const state =
    letter.state === 'preparing' ? (
      <p>{copy.preparing}</p>
    ) : letter.state === 'ready' && letter.createdAt ? (
      <p className="text-sm text-text-muted">
        {format(copy.ready, { when: formatDateTime(locale, letter.createdAt) })}
      </p>
    ) : letter.state === 'failed' && canAsk && !unaskable ? (
      <Notice tone="warning">
        <p>{copy.failed}</p>
      </Notice>
    ) : null
  return (
    <Section title={copy.title}>
      {said}
      <p>{copy.intro}</p>
      {letter.state === 'ready' ? (
        opens ? (
          <>
            <LetterLink fine={fine} />
            {letter.ownerOnly ? <p className="text-sm text-text-muted">{copy.viewLogged}</p> : null}
          </>
        ) : (
          <p className="text-sm text-text-muted">{copy.ownerOnly}</p>
        )
      ) : null}
      {/* Empty, it takes no room: out of the flow, as a screen reader's words are. */}
      <div role="status" className={state ? 'flex flex-col' : 'sr-only'}>
        {state}
      </div>
      {canAsk && unaskable ? (
        <Notice tone="info">
          <p>{copy.renterAnonymised}</p>
        </Notice>
      ) : canAsk && (letter.state === 'none' || letter.state === 'failed') ? (
        <Button variant="staff" className="self-start" busy={busy} onClick={() => void ask()}>
          {letter.state === 'failed' ? copy.retry : copy.prepare}
        </Button>
      ) : null}
      <ProblemNotice problem={problem} />
    </Section>
  )
}

/** The day the renter's data went to the authority, and how: once, and never undone. */
function CommunicationBlock({ fine, onChanged, said }: BlockProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.fines.communication
  const reasonsId = useId()
  const [form, setForm] = useState<{ on: string; channel: FineChannel; reference: string }>(() => ({
    on: romeNow().date,
    channel: 'pec',
    reference: '',
  }))
  const { send, busy, problem } = useSend()
  const reasons = form.on || busy ? [] : [copy.why]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.on || busy) return
    const reference = form.reference.trim()
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/fines/{id}/communicated', {
          params: { path: { id: fine.id } },
          body: { on: form.on, channel: form.channel, ...(reference ? { reference } : {}) },
        }),
      ),
    )
    if (answer) await onChanged(answer.fine, 'communicated')
  }

  const sent = fine.communication
  if (sent) {
    const values = {
      date: formatDate(locale, sent.on),
      channel: m.fines.channels[sent.channel],
      reference: sent.reference ?? '',
      name: sent.by,
    }
    return (
      <Section title={copy.title}>
        {said}
        <p>{format(sent.reference ? copy.doneReference : copy.done, values)}</p>
        {/* The same words and tone as the deadline above (UI-2g): an ended fact, not a warning. */}
        {fine.deadline.state === 'late' ? (
          <p>
            <StatusChip tone={deadlineTone('late')}>{m.fines.deadline.late}</StatusChip>
          </p>
        ) : null}
      </Section>
    )
  }
  if (fine.status !== 'matched') return null
  return (
    <Section title={copy.title}>
      {said}
      <p>{copy.intro}</p>
      {/* Enter in a field sends nothing: what this records is never undone (UI-2g). */}
      <form
        onSubmit={submit}
        onKeyDown={noImplicitSubmit}
        noValidate
        aria-label={copy.title}
        className="flex flex-col gap-3"
      >
        <div className={FIELD_ROW}>
          <Field className={FIELD} label={copy.on} required>
            {(control) => (
              <StaffInput
                {...control}
                type="date"
                value={form.on}
                onChange={(event) => setForm({ ...form, on: event.target.value })}
              />
            )}
          </Field>
          <Field className={FIELD} label={copy.channel} required>
            {(control) => (
              <StaffSelect
                {...control}
                value={form.channel}
                onChange={(event) =>
                  setForm({
                    ...form,
                    channel:
                      FINE_CHANNELS.find((channel) => channel === event.target.value) ?? 'other',
                  })
                }
              >
                {FINE_CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>
                    {m.fines.channels[channel]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
        </div>
        <Field
          label={copy.reference}
          hint={copy.referenceHint}
          optionalLabel={m.newBooking.optional}
        >
          {(control) => (
            <StaffInput
              {...control}
              maxLength={200}
              autoComplete="off"
              value={form.reference}
              onChange={(event) => setForm({ ...form, reference: event.target.value })}
            />
          )}
        </Field>
        <ProblemNotice problem={problem} />
        <div>
          <Button
            type="submit"
            variant="staff"
            busy={busy}
            disabled={reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          >
            {copy.submit}
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </form>
    </Section>
  )
}

/**
 * Decision 4: the admin fee, charged to the rental named at the fee it was
 * handed over with, once; collected from the booking, at the counter or by a
 * payment link, never from a card by itself.
 */
function FeeBlock({ fine, onChanged, said }: BlockProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.fines.fee
  const dialog = useRef<HTMLDialogElement>(null)
  const { send, busy, problem, clear } = useSend()
  const { rental, fee } = fine

  async function charge() {
    const answer = await send(() =>
      call(api.POST('/v1/staff/fines/{id}/fee', { params: { path: { id: fine.id } } })),
    )
    if (!answer) return
    dialog.current?.close()
    await onChanged(answer.fine, 'fee')
  }

  let body: ReactNode
  if (!rental) {
    if (fine.status !== 'registered') return null
    body = <p>{copy.notYet}</p>
  } else if (fee.charged) {
    body = (
      <>
        <p>
          {format(copy.charged, {
            amount: formatEuro(fee.charged.amountCents, locale),
            when: formatDateTime(locale, fee.charged.at),
          })}
        </p>
        <p data-testid="fine-fee-balance">
          {fee.balanceCents !== null && fee.balanceCents > 0
            ? format(copy.balance, {
                code: rental.code,
                amount: formatEuro(fee.balanceCents, locale),
              })
            : format(copy.paid, { code: rental.code })}
        </p>
      </>
    )
  } else if (!fee.rentalFeeCents) {
    body = <p>{copy.none}</p>
  } else if (fine.status === 'matched' || fine.status === 'communicated') {
    const amount = formatEuro(fee.rentalFeeCents, locale)
    body = (
      <>
        <Button
          variant="staff"
          className="self-start"
          onClick={() => {
            clear()
            dialog.current?.showModal()
          }}
        >
          {format(copy.charge, { amount })}
        </Button>
        <ConfirmDialog
          dialogRef={dialog}
          title={copy.title}
          confirmLabel={copy.confirmYes}
          cancelLabel={m.fines.end.cancel}
          busy={busy}
          onConfirm={() => void charge()}
        >
          <p>{format(copy.confirm, { amount, code: rental.code })}</p>
          <ProblemNotice problem={problem} />
        </ConfirmDialog>
      </>
    )
  } else {
    return null
  }
  return (
    <Section title={copy.title}>
      {said}
      {body}
    </Section>
  )
}

const ENDING_LABEL = {
  not_ours: 'notOurs',
  withdrawn: 'withdrawn',
  closed: 'close',
} as const satisfies Record<FineEnding, keyof StaffMessages['fines']['end']>

/** Ruled out, withdrawn by the authority, or closed; a notice ruled out by mistake reopens. */
function EndBlock({ fine, onChanged, said }: BlockProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.fines.end
  const endings = endingsFrom(fine.status)
  const feeCharged = fine.fee.charged !== null
  if (!fine.ended && endings.length === 0 && fine.status !== 'not_ours') return null
  return (
    <Section title={copy.title}>
      {said}
      {fine.ended ? (
        <p>
          {format(fine.ended.reason ? copy.endedReason : copy.ended, {
            status: m.fines.status[fine.status],
            when: formatDateTime(locale, fine.ended.at),
            name: fine.ended.by,
            reason: fine.ended.reason ?? '',
          })}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        {endings.map((to) => (
          <ReasonAction
            key={to}
            label={copy[ENDING_LABEL[to]]}
            required={reasonRequired(to, feeCharged)}
            done="ended"
            onChanged={onChanged}
            request={(reason) =>
              call(
                api.POST('/v1/staff/fines/{id}/end', {
                  params: { path: { id: fine.id } },
                  body: { to, ...(reason ? { reason } : {}) },
                }),
              )
            }
          />
        ))}
        {fine.status === 'not_ours' ? (
          <ReasonAction
            label={copy.reopen}
            required
            done="reopened"
            onChanged={onChanged}
            request={(reason) =>
              call(
                api.POST('/v1/staff/fines/{id}/reopen', {
                  params: { path: { id: fine.id } },
                  body: { reason: reason ?? '' },
                }),
              )
            }
          />
        ) : null}
      </div>
    </Section>
  )
}
